import { createApiClient, getRuntimeConfig, useAuth, type ApiClient } from '@berky/auth'
import { useMemo } from 'react'

/** Die Rolle, die Zugang zur Verwaltung gibt. */
export const PLATFORM_ADMIN_ROLE = 'platform.admin'

/** Eine Rolle einer Person und woher sie kommt. */
export interface RollenHerkunft {
  role: string
  /** `direkt` bei einer Einzelzuweisung, sonst der Name der Gruppe. */
  source: string
}

/** Eine Person, wie die Verwaltung sie zeigt. */
export interface VerwaltungsPerson {
  /** Plattformkennung, oder `null`, wenn die Person noch nie angemeldet war. */
  id: string | null
  /** Kennung im Verzeichnis. */
  externalId: string
  name: string
  email: string
  /** Rollen mit ihrer Herkunft. Leer, solange die Person nie angemeldet war. */
  roles: RollenHerkunft[]
  /** The current directory result does not verify this account’s M365 status. */
  nurLokal: boolean
}

/** Ein Nutzer, wie ihn `GET /users` liefert. */
export interface PlattformNutzer {
  id: string
  externalId: string
  name: string
  email: string
  roles: RollenHerkunft[]
  isBlocked: boolean
}

/** Eine Person aus dem Verzeichnis, wie sie `GET /verzeichnis/personen` liefert. */
export interface VerzeichnisPerson {
  externalId: string
  name: string
  email: string
}

/** A directory page; `fortsetzung` loads the next page. */
export interface VerzeichnisPersonenSeite {
  items: VerzeichnisPerson[]
  fortsetzung: string | null
}

/** Eine Gruppe aus dem Verzeichnis. */
export interface VerzeichnisGruppe {
  id: string
  name: string
}

/** Die Herkunftsangabe für eine direkt zugewiesene Rolle. */
export const DIREKT = 'direkt'

/** Eine verknüpfte Gruppe. */
export interface VerwaltungsGruppe {
  id: string
  name: string
  roles: string[]
  /** Zuletzt lokal bekannte Mitglieder, nicht zwingend der Live-Stand. */
  memberCount: number
}

/** Eine Rolle aus dem Katalog. */
export interface VerwaltungsRolle {
  name: string
  moduleId: string
  description: string
}

/** Wer eine Rolle trägt. */
export interface RollenTraeger {
  userId: string | null
  name: string
  source: string
}

/** Ein Eintrag aus dem Änderungsprotokoll. */
export interface RollenAuditEintrag {
  id: string
  occurredAt: string
  action: string
  roleName: string
  targetType: 'person' | 'gruppe'
  targetName: string
  actorName: string
  actorId: string
}

/** Ein zuletzt bekanntes Mitglied einer verknüpften Gruppe. */
export interface Gruppenmitglied {
  userId: string
  name: string
  email: string
}

/**
 * Der API-Client der Plattform-Endpunkte.
 *
 * Nicht `useApiClient()`: Der kennt seine Basisadresse aus dem Manifest eines
 * Moduls, und die Verwaltung ist kein Modul. Aufbau und Fehlerbehandlung sind
 * dieselben.
 */
export function usePlatformApi(): ApiClient {
  const { getAccessToken, reportApiAuthError } = useAuth()
  const { platformScope } = getRuntimeConfig()
  const scopes = useMemo(() => (platformScope ? [platformScope] : []), [platformScope])

  return useMemo(
    () =>
      createApiClient({
        baseUrl: '/api/platform/identity',
        scopes,
        getAccessToken,
        onAuthError: reportApiAuthError,
      }),
    [scopes, getAccessToken, reportApiAuthError],
  )
}
